import { CLEO_IDENTITY_CSS_VARIABLES } from "./cleo-identity";
import { themeColor, type ThemeColorRole } from "./contract";

/**
 * MetalForge adapter — the boundary between the contract's roles and the
 * frozen `@neucleos/metalforge-web` catalog's generated components
 * (ProgressBar, ThinkingOrbsPill, Loader). Those components take LITERAL
 * colors: hex strings parsed into shader uniforms or canvas fills. A CSS
 * variable never reaches a uniform, so this adapter works in three moves,
 * two of them pure:
 *
 *   1. `createMetalForgeTheme` names the roles as CSS color values — the same
 *      shape the ReactBits and Aceternity adapters hand back.
 *   2. An app-supplied `ColorReader` turns each value into the computed color
 *      at the mount point (getComputedStyle, then a canvas fill so oklch and
 *      color() values become sRGB bytes). The DOM stays outside this package.
 *   3. `resolveMetalForgeProgressInks` / `resolveMetalForgeOrbInks` derive the
 *      component's ink props from the resolved colors. The progress bar's
 *      eight inks are shades of ONE accent over ONE track, so a World's accent
 *      recolors the whole bar and no palette is ever stored (theme law 4).
 *
 * Vendor files stay as generated (theme law 2); the consumer passes these
 * values into the components' existing props.
 */
export interface MetalForgeThemeOptions {
  /** The accent role; absent, the accent order `var(--ncl-accent, var(--ncl-world-accent))` applies. */
  readonly accentRole?: ThemeColorRole;
  /** A literal CSS color value for the accent (e.g. Cleo's identity variable); takes precedence over `accentRole`. */
  readonly accent?: string;
  /** The bar's track and the pill's field. */
  readonly trackRole?: ThemeColorRole;
  /** The orb's dot ink. */
  readonly inkRole?: ThemeColorRole;
  /** The pill's label ink. */
  readonly labelRole?: ThemeColorRole;
  readonly reducedMotion?: boolean;
}

export interface MetalForgeSemanticTheme {
  readonly accent: string;
  readonly track: string;
  readonly ink: string;
  readonly label: string;
  readonly reducedMotion: boolean;
}

/** Cleo's identity as the accent — the value the chat's thinking orb wears. */
export const CLEO_IDENTITY_ACCENT = `var(${CLEO_IDENTITY_CSS_VARIABLES.identity})`;

export function createMetalForgeTheme(
  options: MetalForgeThemeOptions = {},
): MetalForgeSemanticTheme {
  return Object.freeze({
    accent:
      options.accent ??
      (options.accentRole
        ? themeColor(options.accentRole)
        : "var(--ncl-accent, var(--ncl-world-accent))"),
    track: themeColor(options.trackRole ?? "surfaceRaised"),
    ink: themeColor(options.inkRole ?? "text"),
    label: themeColor(options.labelRole ?? "textMuted"),
    reducedMotion: options.reducedMotion === true,
  });
}

/** One sRGB color, 0–255 per channel. */
export type Rgb = readonly [number, number, number];

/**
 * Parses the color forms a computed style or a reader hands back: `#rgb`,
 * `#rrggbb`, `#rrggbbaa`, `rgb()`/`rgba()` in legacy and modern syntax, and
 * `color(srgb r g b)`. Anything else (oklch, named colors) returns null — the
 * reader is expected to normalize those through a canvas first.
 */
export function parseCssColor(value: string): Rgb | null {
  const text = value.trim();
  const hex = /^#([0-9a-f]{3,8})$/i.exec(text);
  if (hex) {
    const digits = hex[1] ?? "";
    if (digits.length === 3 || digits.length === 4) {
      const [r, g, b] = digits.split("").map((d) => parseInt(d + d, 16));
      return [r ?? 0, g ?? 0, b ?? 0];
    }
    if (digits.length === 6 || digits.length === 8) {
      return [
        parseInt(digits.slice(0, 2), 16),
        parseInt(digits.slice(2, 4), 16),
        parseInt(digits.slice(4, 6), 16),
      ];
    }
    return null;
  }
  const rgb = /^rgba?\(\s*([\d.]+%?)[\s,]+([\d.]+%?)[\s,]+([\d.]+%?)/i.exec(text);
  if (rgb) {
    const channel = (raw: string | undefined): number => {
      const token = raw ?? "0";
      const number = parseFloat(token);
      return clampByte(token.endsWith("%") ? (number / 100) * 255 : number);
    };
    return [channel(rgb[1]), channel(rgb[2]), channel(rgb[3])];
  }
  const srgb = /^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/i.exec(text);
  if (srgb) {
    return [
      clampByte(parseFloat(srgb[1] ?? "0") * 255),
      clampByte(parseFloat(srgb[2] ?? "0") * 255),
      clampByte(parseFloat(srgb[3] ?? "0") * 255),
    ];
  }
  return null;
}

function clampByte(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((channel) => clampByte(channel).toString(16).padStart(2, "0")).join("")}`;
}

/** Linear mix in sRGB: `amount` 0 keeps `from`, 1 reaches `to`. */
export function mixRgb(from: Rgb, to: Rgb, amount: number): Rgb {
  const t = Math.max(0, Math.min(1, amount));
  return [
    clampByte(from[0] + (to[0] - from[0]) * t),
    clampByte(from[1] + (to[1] - from[1]) * t),
    clampByte(from[2] + (to[2] - from[2]) * t),
  ];
}

const WHITE: Rgb = [255, 255, 255];

/** Reads one CSS color value at the app's mount point; null when it cannot. */
export type ColorReader = (cssColor: string) => string | null;

/** The generated ProgressBar's `colors` prop, every ink a hex string. */
export interface MetalForgeProgressInks {
  readonly track: string;
  readonly deep: string;
  readonly mid: string;
  readonly glow: string;
  readonly bright: string;
  readonly core: string;
  readonly trail: string;
  readonly trailHot: string;
}

/** The generated ThinkingOrbsPill's ink props, every value a hex string. */
export interface MetalForgeOrbInks {
  readonly accent: string;
  readonly dot: string;
  readonly pill: string;
  readonly label: string;
}

/**
 * The bar's eight inks from one accent over one track: the fill deepens
 * toward the track behind the front and brightens toward white at the
 * front's core, the trail echoes wear the same hue.
 */
export function deriveProgressInks(accent: Rgb, track: Rgb): MetalForgeProgressInks {
  return Object.freeze({
    track: rgbToHex(track),
    deep: rgbToHex(mixRgb(accent, track, 0.62)),
    mid: rgbToHex(accent),
    glow: rgbToHex(mixRgb(accent, WHITE, 0.42)),
    bright: rgbToHex(mixRgb(accent, WHITE, 0.62)),
    core: rgbToHex(mixRgb(accent, WHITE, 0.86)),
    trail: rgbToHex(mixRgb(accent, track, 0.45)),
    trailHot: rgbToHex(mixRgb(accent, WHITE, 0.25)),
  });
}

function readRgb(read: ColorReader, cssColor: string): Rgb | null {
  const value = read(cssColor);
  return value === null ? null : parseCssColor(value);
}

export function resolveMetalForgeProgressInks(
  theme: MetalForgeSemanticTheme,
  read: ColorReader,
): MetalForgeProgressInks | null {
  const accent = readRgb(read, theme.accent);
  const track = readRgb(read, theme.track);
  if (accent === null || track === null) return null;
  return deriveProgressInks(accent, track);
}

export function resolveMetalForgeOrbInks(
  theme: MetalForgeSemanticTheme,
  read: ColorReader,
): MetalForgeOrbInks | null {
  const accent = readRgb(read, theme.accent);
  const dot = readRgb(read, theme.ink);
  const pill = readRgb(read, theme.track);
  const label = readRgb(read, theme.label);
  if (accent === null || dot === null || pill === null || label === null) return null;
  return Object.freeze({
    accent: rgbToHex(accent),
    dot: rgbToHex(dot),
    pill: rgbToHex(pill),
    label: rgbToHex(label),
  });
}
